import {
  Children,
  PropsWithChildren,
  isValidElement,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { cn } from "../../utils/classes";

type ToggleProps = PropsWithChildren<{
  size?: "xs" | "sm" | "md";
  variant?: "outline" | "primary";
  className?: string;
  indicatorClassName?: string;
}>;

const MovingIndicator = ({
  variant,
  size,
  activeLeft,
  activeWidth,
  className,
}: {
  variant: "outline" | "primary";
  size: "xs" | "sm" | "md";
  activeLeft: number;
  activeWidth: number;
  className?: string;
}) => {
  return (
    <div
      className={cn(
        "moving-indicator",
        "absolute top-0 h-full",
        "transition-all duration-500 ease-in-out",
        variant === "outline" && "bg-gray-400",
        size === "md" && "rounded-[5px] text-sm",
        size === "sm" && "rounded-[3px] text-sm",
        size === "xs" && "rounded-[2px] text-xs",
        className,
      )}
      style={{
        width: `${activeWidth}px`,
        left: `${activeLeft}px`,
        backgroundColor:
          variant === "primary" ? "rgba(255, 255, 255, 0.8)" : undefined,
        backdropFilter: variant === "primary" ? "blur(2px)" : undefined,
      }}
    />
  );
};

const Root = ({
  className,
  children,
  variant = "primary",
  size = "md",
  indicatorClassName,
}: ToggleProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [activePos, setActivePos] = useState<{ left: number; width: number }>({ left: 0, width: 0 });

  Children.toArray(children).some((child) => {
    if (!isValidElement(child) || child.type !== Item) {
      console.error("Toggle.Root only accepts Toggle.Item as children");
      return true;
    }
  });

  const activeIndex = Children.toArray(children).findIndex((child) => {
    return isValidElement<ItemProps>(child) && child.props.active;
  });

  useLayoutEffect(() => {
    const container = containerRef.current;
    const wrapper = wrapperRef.current;
    if (!container || !wrapper) return;

    const queryActive = () =>
      container.querySelector('button[data-active="true"]') as HTMLButtonElement | null;

    // Layout offsets (relative to the `relative` wrapper) rather than getBoundingClientRect,
    // so a transformed ancestor (e.g. a popover's zoom-in animation) doesn't skew the indicator.
    const measure = (el: HTMLButtonElement) => {
      setActivePos({ left: el.offsetLeft, width: el.offsetWidth });
    };

    const initialEl = queryActive();
    if (initialEl) {
      requestAnimationFrame(() => measure(initialEl));
    }

    const mutationObserver = new MutationObserver(() => {
      const el = queryActive();
      if (el) requestAnimationFrame(() => measure(el));
    });

    mutationObserver.observe(container, {
      attributes: true,
      subtree: true,
      attributeFilter: ["data-active"],
    });

    const resizeObserver = new ResizeObserver(() => {
      const el = queryActive();
      if (el) measure(el);
    });
    resizeObserver.observe(wrapper);
    resizeObserver.observe(container);

    const onWindowResize = () => {
      const el = queryActive();
      if (el) measure(el);
    };
    window.addEventListener("resize", onWindowResize);

    return () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
      window.removeEventListener("resize", onWindowResize);
    };
  }, [activeIndex, children]);

  return (
    <div
      className={cn(
        "flex items-center overflow-hidden p-0.5 transition-colors duration-300 ease-in focus:outline-hidden focus:ring-offset-2",
        variant === "outline" && "bg-white border border-gray-300",
        variant === "primary" && "bg-gray-300",
        size === "md" && "h-10 rounded-[7px] text-sm",
        size === "sm" && "h-8 rounded-[5px] text-sm",
        size === "xs" && "h-6 rounded-[3px] text-xs",
        "disabled:cursor-not-allowed disabled:bg-gray-400",
        className,
      )}
    >
      <div className="relative size-full" ref={wrapperRef}>
        <div
          className="flex flex-nowrap items-center size-full"
          ref={containerRef}
        >
          {children}
        </div>

        <MovingIndicator
          variant={variant}
          size={size}
          activeLeft={activePos.left}
          activeWidth={activePos.width}
          className={indicatorClassName}
        />
      </div>
    </div>
  );
};

type ItemProps = PropsWithChildren<{
  active: boolean;
  onClick: () => void;
  className?: string;
  variant?: "outline" | "primary";
}>;

const Item = ({ active, onClick, children, className, variant = "outline" }: ItemProps) => {
  return (
    <button
      type="button"
      data-active={active ? "true" : undefined}
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "flex h-full cursor-pointer select-none items-center justify-center gap-1.5 truncate px-4 text-center font-semibold rounded-xxs",
        !active && "transition-colors duration-300 ease-in", // we want transition only when not active to not overlap the backgrounds
        variant === "outline" && [
          "text-gray-900",
          !active && "hover:bg-gray-300",
        ],
        variant === "primary" && [
          "text-gray-900",
          !active && "hover:bg-gray-100",
        ],
        active && "z-[1]",
        className,
      )}
    >
      {children}
    </button>
  );
};

export const Toggle = Object.assign(Root, { Item });
